<template>
  <mp-box>
    <page-header title="Calibration groups" :breadcrumbs="breadcrumbs">
      <template #action>
        <button-create title="Create new group" @open="onOpenForm" />
      </template>
    </page-header>
    <content-wrapper>
      <template #content>
        <mp-box max-w="100%" w="520px">
          Group your contributors into calibration groups to define their scopes and determine which employees they can calibrate.
        </mp-box>
        <!-- Filter -->
        <mp-flex gap="2" my="6">
          <mp-flex ml="auto" w="280px" align-items="center">
            <input-search
              :search="search_data"
              :is-loading="isLoading"
              placeholder="Search group name"
              modify-class=""
              is-full-width
              @input="search_data = $event"
            />
          </mp-flex>
        </mp-flex>
        <!-- Table -->
        <mp-table-container width="100%" overflow="scroll">
          <mp-table>
            <mp-table-head>
              <mp-table-row>
                <mp-table-cell as="th" scope="col" width="280px" min-width="280px" @click="updateSort('name')">
                  Group name <mp-icon :name="getIcon('name')" />
                </mp-table-cell>
                <mp-table-cell as="th" scope="col">
                  Scopes
                </mp-table-cell>
                <mp-table-cell as="th" scope="col">
                  Contributors
                </mp-table-cell>
                <mp-table-cell as="th" scope="col" w="130px" />
              </mp-table-row>
            </mp-table-head>
            <table-skeleton v-if="isLoading" :rows="5" :columns="4" />
            <mp-table-body v-else>
              <mp-table-row v-for="item in groups" :key="item.uuid">
                <mp-table-cell width="40%" min-w="340px" as="td" scope="row" white-space="normal" word-break="break-word">
                  {{ item.name || '-' }}
                </mp-table-cell>
                <mp-table-cell as="td" scope="row">
                  <mp-tag
                    v-if="item.is_apply_all_scopes"
                    v-mp-tooltip="'This group can calibrate all employee results in this calibration.'"
                    size="sm"
                    >All scopes</mp-tag
                  >
                  <mp-flex v-else-if="item.scopes" gap="2" flex-wrap="wrap" max-w="360px">
                    <template v-for="(scope, key) in item.scopes">
                      <mp-tag
                        v-if="scope && scope.total"
                        :key="key"
                        v-mp-tooltip="`${scope.items.join(', ')}${scope.total > scope.items.length ? ' and more.' : ''}`"
                        size="sm"
                        white-space="nowrap"
                      >
                        {{ key | formatScopeKey }} ({{ formatCurrency(scope.total) }})
                      </mp-tag>
                    </template>
                  </mp-flex>
                  <template v-else>-</template>
                </mp-table-cell>
                <mp-table-cell as="td" scope="row" min-w="240px">
                  <mp-avatar-group v-if="item.contributors && item.contributors.total && item.contributors.items">
                    <template v-for="(cont, idx) in item.contributors.items">
                      <mp-avatar
                        v-if="idx < MAX_DISPLAYED_CONTRIBUTOR"
                        :key="cont.user_id"
                        v-mp-tooltip="cont.name"
                        :name="cont.name"
                        :src="cont.avatar"
                      />
                    </template>
                    <mp-avatar
                      v-if="item.contributors.total > MAX_DISPLAYED_CONTRIBUTOR"
                      background-color="gray"
                      :name="getExtraContributorsLabel(item.contributors.total)"
                    />
                  </mp-avatar-group>
                  <template v-else>-</template>
                </mp-table-cell>
                <mp-table-cell
                  as="td"
                  scope="row"
                  text-align="right"
                  width="130px"
                  min-width="130px"
                  top="0"
                  right="0"
                  box-shadow="inset 1px 0px #D0D6DD"
                  is-fixed
                >
                  <mp-popover :id="`popover-${item.uuid}`" use-portal>
                    <mp-popover-trigger>
                      <mp-button variant="outline" right-icon="caret-down" :data-qa="`btn-group-actions-${item.uuid}`">Actions</mp-button>
                    </mp-popover-trigger>
                    <mp-popover-content max-width="164px" bg="white" rounded="md" shadow="lg" border-width="1px" border-color="gray.400">
                      <mp-popover-list>
                        <mp-popover-list-item :data-qa="`option-edit-group-${item.uuid}`" @click="onOpenForm(item)">View & edit</mp-popover-list-item>
                        <mp-popover-list-item
                          color="var(--colors-red-400)!important"
                          :data-qa="`option-delete-group-${item.uuid}`"
                          @click="onDeleteGroup(item)"
                          >Delete group</mp-popover-list-item
                        >
                      </mp-popover-list>
                    </mp-popover-content>
                  </mp-popover>
                </mp-table-cell>
              </mp-table-row>
            </mp-table-body>
          </mp-table>
        </mp-table-container>
        <mp-box v-if="!isLoading && !groups.length" py="5">
          <blank-slate :key="blankSlateData.key" is-small :blank-slate-data="blankSlateData" />
        </mp-box>
        <pagination v-if="groups.length && metas.total > 10" :metas="metas" :disabled="isLoading" @update="onPagination" />
        <modal-confirmation :is-open.sync="isModalDelete" title="Delete group" :is-loading="isLoadingDelete" size="sm" @confirm="deleteGroup">
          <template #content>
            <mp-box>
              <mp-text mb="3"
                >Are you sure you want to delete
                <mp-text v-if="selectedGroup" as="span" font-weight="semibold">{{ selectedGroup.name }}</mp-text> group?</mp-text
              >
              <mp-text>All data and settings within this group will be permanently removed. This action cannot be undone.</mp-text>
            </mp-box>
          </template>
        </modal-confirmation>
        <drawer-group-form :is-open.sync="isModalForm" :uuid="selectedGroup ? selectedGroup.uuid : ''" @updated="fetch" />
      </template>
    </content-wrapper>
  </mp-box>
</template>

<script>
import { debounce } from 'lodash-es';
import PageHeader from '@/components/layouts/PageHeader';
import ButtonCreate from '@/components/layouts/ButtonCreate';
import ContentWrapper from '@/components/layouts/ContentWrapper';
import TableSkeleton from '@/components/general/TableSkeleton';
import BlankSlate from '@/components/general/BlankSlate';
import Pagination from '@/components/Pagination';
import InputSearch from '@/components/InputSearch';
import DrawerGroupForm from '@/views/reviews/calibration/master-group/DrawerGroupForm';
import { buildParams } from '@/helpers/url';
import { formatCurrency } from '@/helpers/number';
import ModalConfirmation from '@/components/ModalConfirmation';
import { sortMixin } from '@/mixins/table';
import { alertMixin } from '@/mixins/general';

const MAX_DISPLAYED_CONTRIBUTOR = 5;

export default {
  name: 'ReviewsCalibrationMasterGroupIndex',
  filters: {
    formatScopeKey(val) {
      if (!val) return '';
      if (val === 'sbu') return 'SBU';
      return val.charAt(0).toUpperCase() + val.slice(1).replace('_', ' ');
    },
  },
  components: {
    PageHeader,
    ButtonCreate,
    ContentWrapper,
    Pagination,
    InputSearch,
    ModalConfirmation,
    BlankSlate,
    TableSkeleton,
    DrawerGroupForm,
  },
  mixins: [sortMixin, alertMixin],
  data() {
    return {
      breadcrumbs: [{ label: 'Calibration', url: { name: 'calibration_list' } }],
      MAX_DISPLAYED_CONTRIBUTOR,
      isLoading: false,
      isLoadingDelete: false,
      isModalDelete: false,
      isModalForm: false,
      search_data: '',
      metas: {
        current_page: 1,
        last_page: 1,
        per_page: 10,
        from: 1,
        to: 1,
        total: 1,
      },
      groups: [],
      selectedGroup: '',
    };
  },
  watch: {
    search_data: debounce(function() {
      this.metas.current_page = 1;
      this.fetch();
    }, 700),
    '$route.query.is_create': {
      immediate: true,
      handler(val) {
        if (val === '1') {
          this.isModalForm = true;
        }
      },
    },
  },
  computed: {
    isFilter() {
      return this.search_data;
    },
    blankSlateData() {
      if (this.isFilter) {
        return {
          key: 'no-group-result',
          title: 'No group result found',
          sub_title: 'Check the keyword and try to search again.',
          img: this.$store.state.assets.searchNotFoundImg,
        };
      }
      return {
        key: 'no-group-data',
        title: 'No calibration group yet',
        sub_title: 'Calibration group list will appear here.',
        img: this.$store.state.assets.emptyGroupImg,
      };
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    formatCurrency,
    onPagination({ page, limit }) {
      this.metas.current_page = page;
      this.metas.per_page = limit;
      this.fetch();
    },
    async fetch() {
      try {
        this.groups = [];
        this.isLoading = true;
        const body = {
          search: this.search_data,
          page: this.metas.current_page,
          limit_per_page: this.metas.per_page,
          sort_field: this.sort,
        };
        const { data, response } = await this.$axios.get(`${this.$newEndPoint}/calibration/groups?${buildParams(body)}`);

        if (data?.status_code === 200 && data?.contents?.data) {
          this.groups = data.contents.data;
          const { total, from, to, last_page } = data.contents.meta;
          this.metas = {
            ...this.metas,
            total,
            from,
            to,
            last_page,
          };
        } else throw Error(response?.data?.message);
      } catch (e) {
        this.alertToast(e.message || 'Failed to fetch groups', 'danger');
      } finally {
        this.isLoading = false;
      }
    },
    onOpenForm(group) {
      this.isModalForm = true;
      this.selectedGroup = group || '';
    },
    onDeleteGroup(group) {
      this.isModalDelete = true;
      this.selectedGroup = group || '';
    },
    async deleteGroup() {
      try {
        this.isLoadingDelete = true;
        const { data } = await this.$axios.delete(`${this.$newEndPoint}/calibration/groups/${this.selectedGroup?.uuid}`);
        if (data.status_code !== 200) throw Error;
        this.isModalDelete = false;
        this.fetch();
        this.alertToast('Group deleted', 'success');
      } catch (e) {
        this.alertToast('Failed to delete group', 'danger');
      } finally {
        this.isLoadingDelete = false;
      }
    },
    updateSort(field) {
      if (!this.isLoading) {
        this.updateFilterSort(field);
        this.fetch();
      }
    },
    getExtraContributorsLabel(totalContributors) {
      const extraCount = totalContributors - MAX_DISPLAYED_CONTRIBUTOR;
      return extraCount > 9 ? '9 +' : `+ ${extraCount}`;
    },
  },
};
</script>
